import { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { X, Upload, Plus, Trash2, Loader, CheckCircle, AlertTriangle } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Candidate {
  id: string
  name: string
  email: string
  allSkills?: string[]
  resumeUrl?: string
}

interface CandidateEditModalProps {
  open: boolean
  onClose: () => void
  candidate: Candidate | null
  onSuccess?: () => void
}

export default function CandidateEditModal({ open, onClose, candidate, onSuccess }: CandidateEditModalProps) {
  const [loading, setLoading] = useState(false)
  const [uploadingResume, setUploadingResume] = useState(false)
  const [skills, setSkills] = useState<string[]>(candidate?.allSkills || [])
  const [newSkill, setNewSkill] = useState('')
  const [message, setMessage] = useState<{text: string, type: 'success' | 'error' | 'info'} | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!open || !candidate) return null

  const handleResumeUpload = async (file: File) => {
    if (!candidate) return

    setUploadingResume(true)
    setMessage(null)

    try {
      const formData = new FormData()
      formData.append('resume', file)
      formData.append('candidate_id', candidate.id)

      const response = await apiClient.uploadCandidateResume(candidate.id, formData)
      
      if (response.success) {
        setMessage({
          text: 'Resume uploaded successfully!',
          type: 'success'
        })
        onSuccess?.()
      } else {
        setMessage({
          text: response.error || 'Failed to upload resume',
          type: 'error'
        })
      }
    } catch (error: any) {
      console.error('Error uploading resume:', error)
      setMessage({
        text: error?.message || 'Failed to upload resume',
        type: 'error'
      })
    } finally {
      setUploadingResume(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type (PDF, DOC, DOCX)
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        setMessage({
          text: 'Please upload a PDF, DOC, or DOCX file',
          type: 'error'
        })
        return
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setMessage({
          text: 'File size must be less than 10MB',
          type: 'error'
        })
        return
      }

      handleResumeUpload(file)
    }
  }

  const handleAddSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()])
      setNewSkill('')
    }
  }

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter(skill => skill !== skillToRemove))
  }

  const handleSaveSkills = async () => {
    if (!candidate) return

    setLoading(true)
    setMessage(null)

    try {
      const response = await apiClient.updateCandidateSkills(candidate.id, { skills })
      
      if (response.success) {
        setMessage({
          text: 'Skills updated successfully!',
          type: 'success'
        })
        onSuccess?.()
      } else {
        setMessage({
          text: response.error || 'Failed to update skills',
          type: 'error'
        })
      }
    } catch (error: any) {
      console.error('Error updating skills:', error)
      setMessage({
        text: error?.message || 'Failed to update skills',
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSkills(candidate?.allSkills || [])
    setNewSkill('')
    setMessage(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Candidate: {candidate.name}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200'
                : message.type === 'info'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertTriangle size={16} />
            )}
            <span className="text-sm">{message.text}</span>
          </motion.div>
        )}

        <div className="space-y-6">
          {/* Resume Upload Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Resume Upload</h3>
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingResume}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingResume ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Upload size={16} />
                )}
                {uploadingResume ? 'Uploading...' : 'Upload Resume'}
              </button>
              
              <p className="text-sm text-gray-600">
                Supported formats: PDF, DOC, DOCX (max 10MB)
              </p>
              
              {candidate.resumeUrl && (
                <div className="text-sm text-green-600">
                  ✓ Resume already uploaded
                </div>
              )}
            </div>
          </div>

          {/* Skills Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Skills</h3>
            
            {/* Add new skill */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                placeholder="Add a skill..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddSkill}
                disabled={!newSkill.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Skills list */}
            <div className="space-y-2">
              {skills.length === 0 ? (
                <p className="text-gray-500 text-sm">No skills added yet</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      <span>{skill}</span>
                      <button
                        onClick={() => handleRemoveSkill(skill)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleSaveSkills}
              disabled={loading}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin inline mr-2" />
                  Saving...
                </>
              ) : (
                'Save Skills'
              )}
            </button>
          </div>
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  )
}